import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { Send, Loader2 } from "lucide-react";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";

const inputCls = "w-full border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
const labelCls = "text-xs tracking-[0.2em] uppercase font-bold text-slate-500";
const primaryBtn = "bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-60";

export default function TestAutomation() {
  const { activeBranchId } = useBranch();
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [mobile, setMobile] = useState("");
  const [variablesText, setVariablesText] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadTemplates = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      const res = await api.get("/templates", { params: { branch_id: activeBranchId } });
      setTemplates(res.data || []);
    } catch (e) {
      // ignore
    }
  }, [activeBranchId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const selectedTemplate = templates.find((t) => t.name === templateName);

  async function sendTest() {
    if (!templateName) {
      toast.error("Select a template first.");
      return;
    }
    if (!mobile.trim()) {
      toast.error("Enter a test mobile number.");
      return;
    }

    const variables = variablesText
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    setSending(true);
    setLastResult(null);

    try {
      const res = await api.post("/whatsapp/send", {
        branch_id: activeBranchId,
        mobile: mobile.trim(),
        template_name: templateName,
        variables,
        language: selectedTemplate?.language || "en",
      });
      setLastResult({ ok: true, data: res.data });
      toast.success("Test message sent.");
    } catch (e) {
      setLastResult({ ok: false, data: e?.response?.data || { message: e.message } });
      toast.error("Send failed. See details below.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div data-testid="whatsapp-test-page">
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Template</label>
        <Select value={templateName} onValueChange={setTemplateName}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select a template" />
          </SelectTrigger>
          <SelectContent>
            {templates.map((t) => (
              <SelectItem key={t.name} value={t.name}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedTemplate && (
        <div className="text-xs bg-white/5 border border-white/10 rounded-md p-3 text-slate-300 whitespace-pre-wrap">
          {selectedTemplate.body || selectedTemplate.components?.[0]?.text || "No preview available."}
        </div>
      )}

      <div>
        <label className={labelCls}>Test mobile (with country code)</label>
        <input
          className={`${inputCls} mt-1`}
          placeholder="919876543210"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
        />
      </div>

      <div>
        <label className={labelCls}>Variables (comma separated, in order)</label>
        <input
          className={`${inputCls} mt-1`}
          placeholder="Test Member, Monthly, 20-08-2026"
          value={variablesText}
          onChange={(e) => setVariablesText(e.target.value)}
        />
      </div>

      <button className={primaryBtn} onClick={sendTest} disabled={sending}>
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {sending ? "Sending..." : "Send test"}
      </button>

      {lastResult && (
        <div
          className={`text-xs rounded-md p-3 border whitespace-pre-wrap ${
            lastResult.ok ? "bg-green-500/10 border-green-500/30 text-green-400" : "bg-red-500/10 border-red-500/30 text-red-400"
          }`}
        >
          {JSON.stringify(lastResult.data, null, 2)}
        </div>
      )}
    </div>
    </div>
  );
}
